import { useTranslation } from "@shared/utils/internationalization";
import { useColorMode } from "@shared/hooks/useColorMode";
import Text from "@ui-kit/typography/Text/Text.web";
import { GOOGLE_PLACES_API_KEY } from "@shared/env";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import React from "react";
import { GoogleMap } from "./GoogleMap.web";
import { GoogleMapMarker } from "./GoogleMapMarker.web";

export interface GoogleMapContainerProps {
  latLngPosition: string;
  mapZoom?: number;
  mapStyle?: { [key: string]: string };
  displayMarker?: boolean;
}

/**
 * Displays an interactive Google Map element and an optional marker
 * Automatically syncs with the app's color mode (light/dark)
 */
export const GoogleMapContainer: React.FC<GoogleMapContainerProps> = ({
  latLngPosition,
  mapZoom = 15,
  mapStyle = { height: "340px", width: "100%", borderRadius: "10px" },
  displayMarker = false,
}) => {
  const { t } = useTranslation("common");
  const { activeTheme } = useColorMode();

  // Map colorMode to Google Maps colorScheme format
  const mapColorScheme = activeTheme === "dark" ? "DARK" : "LIGHT";

  // converts the input position from a string to an object of lat/lng typing
  const getValidPositionFormat = (
    position: string
  ): google.maps.LatLngLiteral => {
    const splitPositionString = position.split(",");
    const parsePositionArray = splitPositionString.map((int) =>
      parseFloat(int)
    );

    return { lat: parsePositionArray[0], lng: parsePositionArray[1] };
  };

  // Renders a loading message or error message, depending on the map's current status
  const render = (status: Status): JSX.Element => {
    switch (status) {
      case Status.FAILURE:
        return <Text color="secondary.600">{t("Unable to load map.")}</Text>;
      default:
        return <Text>{t("Loading map...")}.</Text>;
    }
  };

  return (
    <Wrapper apiKey={GOOGLE_PLACES_API_KEY} render={render}>
      <GoogleMap
        center={getValidPositionFormat(latLngPosition)}
        zoom={mapZoom}
        style={mapStyle}
        colorScheme={mapColorScheme}
      >
        {displayMarker && (
          <GoogleMapMarker position={getValidPositionFormat(latLngPosition)} />
        )}
      </GoogleMap>
    </Wrapper>
  );
};

export default GoogleMapContainer;
